/**
 * DateR — date and date_partial field renderer.
 * Full dates use input[type=date]; partial dates use a text input with pattern.
 */

import { useFormContext } from 'react-hook-form';
import { DateTextField } from '../../../components/intake/fields';
import type { FieldDef, RendererProps } from '../types';
import { buildValidationRules } from '../validation/fromSchema';
import { resolveLabel, resolveHelpText, deriveFieldName, formatDateForDisplay } from '../utils';
import { lookupFieldTooltip } from '../../fieldTooltips';

interface Props extends RendererProps {
  field: FieldDef;
}

export function DateR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { getValues } = useFormContext();
  const name = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);
  const isPartial = field.type === 'date_partial';

  if (pdfMode) {
    const value = getValues(name);
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">
          {value ? formatDateForDisplay(value as string) : <span className="text-slate-400 italic">Not provided</span>}
        </p>
      </div>
    );
  }

  return (
    <DateTextField
      name={name}
      label={label}
      hint={resolveHelpText(field, audience) ?? (isPartial ? 'Month and year is enough, e.g. 03/2021. The exact day is not needed.' : 'Type the date as DD/MM/YYYY')}
      required={field.required}
      rules={buildValidationRules(field)}
      partial={isPartial}
      placeholder={isPartial ? 'MM/YYYY' : 'DD/MM/YYYY'}
      tooltip={lookupFieldTooltip(field.canonical_name)}
    />
  );
}
